import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Download } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import GuideDialog from "@/components/GuideDialog";
import ProductCard from "@/components/ProductCard";
import { PRODUCTS } from "@/data/products";
import { ELEMENTS, getElement, AREAS_SERVED } from "@/data/elements";
import { INSIGHTS, getElementGuide } from "@/data/insights";
import { useStudio } from "@/context/StudioContext";
import { useSEO, breadcrumb, faqPage, SITE } from "@/lib/useSEO";

const areaServedSchema = () => [
  ...AREAS_SERVED.local.map((n) => ({ "@type": "City", name: n })),
  ...AREAS_SERVED.inner.map((n) => ({ "@type": "City", name: n })),
  ...AREAS_SERVED.wider.map((n) => ({ "@type": "City", name: n })),
  ...AREAS_SERVED.regions.map((n) => ({ "@type": "AdministrativeArea", name: n })),
];

const Eyebrow = ({ children }) => (
  <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase">{children}</p>
);

/**
 * Element — /timber · /carpet · /tile · /stone
 * High-intent local landing page per element: hero, editorial intro, spec
 * notes, that element's products, FAQs and a Sydney/NSW service block.
 */
export default function Element({ slug }) {
  const el = getElement(slug);
  const { openBooking, openSample } = useStudio();
  const [guideOpen, setGuideOpen] = useState(false);

  const items = useMemo(
    () => PRODUCTS.filter((p) => p.discipline === el?.discipline),
    [el]
  );

  const path = `/${slug}`;
  const faqs = el?.faqs || [];

  useSEO({
    title: el?.seo.title,
    description: el?.seo.description,
    keywords: el?.seo.keywords,
    path,
    image: el?.hero?.startsWith("http") ? el.hero : `${SITE}${el?.hero}`,
    imageAlt: el?.heroAlt,
    jsonLd: el
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${SITE}${path}#service`,
            name: `${el.offerName} — Sydney`,
            serviceType: el.offerName,
            description: el.seo.description,
            url: `${SITE}${path}`,
            provider: {
              "@type": "LocalBusiness",
              "@id": `${SITE}/#studio`,
              name: "My Floor Studio",
              telephone: "+61272467899",
              address: {
                "@type": "PostalAddress",
                streetAddress: "184 Victoria Road",
                addressLocality: "Drummoyne",
                addressRegion: "NSW",
                postalCode: "2047",
                addressCountry: "AU",
              },
            },
            areaServed: areaServedSchema(),
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `${el.name} — My Floor Studio`,
            numberOfItems: items.length,
            // Plain ListItems on purpose: `Product` without a published price
            // is rejected by Google's product-snippet validation.
            itemListElement: items.map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: `${p.name} · ${p.discipline}`,
              url: `${SITE}/collection/${p.id}`,
            })),
          },
          faqPage(faqs),
          breadcrumb([
            { name: "Home", path: "/" },
            { name: el.name, path },
          ]),
        ]
      : undefined,
  });

  if (!el) return <Navigate to="/collection" replace />;

  const others = ELEMENTS.filter((e) => e.slug !== el.slug);
  const guide = getElementGuide(el.name);
  const otherReads = INSIGHTS.filter((a) => a.slug !== guide?.slug).slice(0, 3);
  const heroSrc = el.hero;

  return (
    <div className="min-h-screen bg-[#121315] grain" data-testid={`element-page-${el.slug}`}>
      <Nav />

      {/* Hero */}
      <section className="relative h-[76vh] min-h-[540px] w-full overflow-hidden">
        <img
          src={heroSrc}
          alt={el.heroAlt}
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to top, rgba(18,19,21,0.96) 0%, rgba(18,19,21,0.55) 45%, rgba(18,19,21,0.62) 100%)",
          }}
        />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12 pb-14 md:pb-20">
            <motion.div
              initial={{ opacity: 0, y: 26 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Eyebrow>The Element · {el.name}</Eyebrow>
              <h1 className="mt-5 font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.98] max-w-4xl">
                {el.h1}
              </h1>
              <p className="mt-6 text-[#D6D1CA]/85 font-body text-sm md:text-base max-w-2xl leading-relaxed">
                {el.lede}
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <button
                  data-testid={`element-${el.slug}-book-btn`}
                  onClick={openBooking}
                  className="btn-bronze rounded-full px-7 h-11 text-[11px] tracking-[0.18em] uppercase font-medium"
                >
                  Request Appointment
                </button>
                <button
                  data-testid={`element-${el.slug}-sample-btn`}
                  onClick={openSample}
                  className="rounded-full border border-[#D6D1CA]/30 px-7 h-11 text-[11px] tracking-[0.18em] uppercase text-[#D6D1CA] hover:text-[#F5F4F0] hover:border-[#C9A876]"
                  style={{ transition: "color .4s ease, border-color .4s ease" }}
                >
                  Request a Sample
                </button>
                <Link
                  to="/visualise"
                  data-testid={`element-${el.slug}-visualise-link`}
                  className="text-[#C9A876] text-[11px] tracking-[0.25em] uppercase link-underline"
                >
                  See it in a room →
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Editorial intro + spec notes */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7 space-y-7">
          {el.body.map((p, i) => (
            <p
              key={i}
              className="text-[#D6D1CA]/80 font-body text-sm md:text-base leading-[1.85]"
            >
              {p}
            </p>
          ))}
          <div className="pt-6 grid sm:grid-cols-3 gap-8">
            {el.highlights.map((h) => (
              <div key={h.title} className="border-t border-[#8C7355]/25 pt-5">
                <h3 className="font-serif-display text-[#F5F4F0] text-lg font-light leading-snug">
                  {h.title}
                </h3>
                <p className="text-[#D6D1CA]/65 text-xs mt-2.5 font-body leading-relaxed">
                  {h.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="border border-[#8C7355]/20 bg-[#1a1b1e]/50 p-8">
            <Eyebrow>Specification notes</Eyebrow>
            <dl className="mt-7 divide-y divide-[#8C7355]/15">
              {el.specNotes.map((s) => (
                <div key={s.label} className="py-4 first:pt-0">
                  <dt className="text-[#C9A876]/85 text-[9px] tracking-[0.28em] uppercase">
                    {s.label}
                  </dt>
                  <dd className="text-[#D6D1CA]/85 text-sm font-body mt-1.5">{s.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </aside>
      </section>

      {/* Products of this element */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-28">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <Eyebrow>In the studio</Eyebrow>
            <h2 className="mt-5 font-serif-display text-[#F5F4F0] font-light tracking-tight text-3xl md:text-5xl">
              {el.name} in the collection
            </h2>
          </div>
          <Link
            to="/collection"
            data-testid={`element-${el.slug}-collection-link`}
            className="text-[#C9A876] text-[11px] tracking-[0.25em] uppercase link-underline whitespace-nowrap"
          >
            View the full collection →
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* FAQs */}
      <section className="border-t border-[#8C7355]/15 bg-[#0f1012]">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 py-24 md:py-32">
          <Eyebrow>Common questions</Eyebrow>
          <h2 className="mt-5 font-serif-display text-[#F5F4F0] font-light tracking-tight text-3xl md:text-5xl">
            {el.name}, answered
          </h2>
          <div className="mt-12 divide-y divide-[#8C7355]/15 border-t border-[#8C7355]/15">
            {faqs.map((f, i) => (
              <details
                key={f.q}
                data-testid={`element-${el.slug}-faq-${i}`}
                className="group py-6"
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <h3 className="font-serif-display text-[#F5F4F0] text-lg md:text-xl font-light leading-snug">
                    {f.q}
                  </h3>
                  <span className="text-[#C9A876] text-xl leading-none mt-1 shrink-0 group-open:rotate-45"
                    style={{ transition: "transform .35s ease" }}>
                    +
                  </span>
                </summary>
                <p className="text-[#D6D1CA]/75 text-sm font-body leading-[1.85] mt-4 max-w-3xl">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Local service block */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-28 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Eyebrow>Where we work</Eyebrow>
          <h2 className="mt-5 font-serif-display text-[#F5F4F0] font-light tracking-tight text-3xl md:text-4xl leading-tight">
            {el.name} across Sydney<br />and New South Wales
          </h2>
        </div>
        <div className="lg:col-span-7 space-y-6">
          <p className="text-[#D6D1CA]/80 font-body text-sm md:text-base leading-[1.85]">
            The studio is at 184 Victoria Road, Drummoyne — five minutes from Five Dock and
            Russell Lea, ten from Balmain, Rozelle and Concord, and an easy run from the Lower
            North Shore and the Eastern Suburbs. Consultations are by appointment, Monday to
            Saturday, so the room is yours for the hour.
          </p>
          <p className="text-[#D6D1CA]/60 font-body text-sm leading-[1.9]">
            Regularly specifying {el.name.toLowerCase()} for projects in{" "}
            {[...AREAS_SERVED.local, ...AREAS_SERVED.inner, ...AREAS_SERVED.wider].join(", ")}, and
            for architects and builders across {AREAS_SERVED.regions.join(", ")}.
          </p>
          <Link
            to="/contact"
            data-testid={`element-${el.slug}-contact-link`}
            className="inline-block text-[#C9A876] text-[11px] tracking-[0.25em] uppercase link-underline"
          >
            Studio details & directions →
          </Link>
        </div>
      </section>

      {/* Cross-links: other elements + journal */}
      <section className="border-t border-[#8C7355]/15">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-20">
          <Eyebrow>The other elements</Eyebrow>
          <div className="mt-8 grid sm:grid-cols-3 gap-px bg-[#8C7355]/15">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/${o.slug}`}
                data-testid={`element-link-${o.slug}`}
                className="group bg-[#121315] px-7 py-9 flex items-center justify-between hover:bg-[#1a1b1e]"
                style={{ transition: "background-color .4s ease" }}
              >
                <span>
                  <span className="font-serif-display text-[#F5F4F0] text-2xl md:text-3xl font-light group-hover:text-[#C9A876]"
                    style={{ transition: "color .4s ease" }}>
                    {o.name}
                  </span>
                  <span className="block text-[#D6D1CA]/50 text-[10px] tracking-[0.28em] uppercase mt-2">
                    {o.caption}
                  </span>
                </span>
                <ArrowUpRight size={20} className="text-[#C9A876] opacity-40 group-hover:opacity-100"
                  style={{ transition: "opacity .4s ease" }} />
              </Link>
            ))}
          </div>

          <div className="mt-16">
            <Eyebrow>From the Journal</Eyebrow>
            {guide && (
              <Link
                to={`/insights/${guide.slug}`}
                data-testid={`element-${el.slug}-guide-link`}
                className="group mt-6 grid sm:grid-cols-12 gap-6 items-center border border-[#8C7355]/20 hover:border-[#8C7355]/45 p-5"
                style={{ transition: "border-color .4s ease" }}
              >
                <div className="sm:col-span-4 aspect-[16/10] overflow-hidden bg-[#0f1012]">
                  <img
                    src={guide.heroImage}
                    alt={guide.heroAlt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03]"
                    style={{ transition: "transform .7s ease" }}
                  />
                </div>
                <div className="sm:col-span-8">
                  <span className="text-[#C9A876] text-[10px] tracking-[0.3em] uppercase">
                    {guide.category} · {guide.readMins} min read
                  </span>
                  <h3 className="font-serif-display italic text-[#F5F4F0] text-2xl md:text-3xl font-light mt-2">
                    {guide.title}
                  </h3>
                  <p className="text-[#D6D1CA]/65 text-sm font-body mt-2 max-w-xl">{guide.excerpt}</p>
                  <span className="inline-block mt-4 text-[#C9A876] text-[10px] tracking-[0.28em] uppercase link-underline">
                    Read the {el.name.toLowerCase()} guide →
                  </span>
                </div>
              </Link>
            )}
            {guide?.pdf && (
              <button
                type="button"
                onClick={() => setGuideOpen(true)}
                data-testid={`element-${el.slug}-guide-pdf-btn`}
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#8C7355]/50 px-6 py-3 text-[10px] tracking-[0.25em] uppercase text-[#C9A876] hover:border-[#C9A876] hover:text-[#dcb884]"
                style={{ transition: "color .4s ease, border-color .4s ease" }}
              >
                <Download size={13} strokeWidth={1.8} />
                Download the {el.name.toLowerCase()} guide (PDF)
              </button>
            )}
            <ul className="mt-8 space-y-3">
              {otherReads.map((a) => (
                <li key={a.slug}>
                  <Link
                    to={`/insights/${a.slug}`}
                    data-testid={`element-${el.slug}-journal-${a.slug}`}
                    className="link-underline text-[#D6D1CA]/80 text-sm font-body hover:text-[#F5F4F0]"
                    style={{ transition: "color .4s ease" }}
                  >
                    {a.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <GuideDialog open={guideOpen} onOpenChange={setGuideOpen} guide={guide} />
      <Footer />
    </div>
  );
}
